import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import axios from "axios";
import { SESSION_ABORT, SESSION_ACTIVATE, SESSION_COMPLETE, USER } from "../../config/constants";
import socket, { APP_UPDATE_AVAILABLE } from "../../socket";
import { getFieldErrors, IFieldError } from "../helpers";

export interface IRentalItemAttachmentSessionSession {      //TODO: remove this from code if not needed
    _id: string;
    rentalItemAttachmentSessionId: string;
    creationTimeStamp: number;
    activationTimeStamp?: number;
    abortionTimeStamp?: number;
    completionTimeStamp?: number;
}

class RegRentalItemAttachmentSessionErrs {
    form: string | null = null;
}

export interface IRentalItemsAttachmentsSessionsState {
    registeredRentalItemAttachmentSession: boolean;
    registeringRentalItemAttachmentSession: boolean;
    regRentalItemAttachmentSessionErrs: RegRentalItemAttachmentSessionErrs;
    togglingRentalItemAttachmentSessionStatus: boolean;
    toggleRentalItemAttachmentSessionStatusErr: string | null;
    toggledRentalItemAttachmentSessionStatus: boolean;
}

const initialState = {
    registeredRentalItemAttachmentSession: false,
    registeringRentalItemAttachmentSession: false,
    regRentalItemAttachmentSessionErrs: new RegRentalItemAttachmentSessionErrs(),
    togglingRentalItemAttachmentSessionStatus: false,
    toggleRentalItemAttachmentSessionStatusErr: null,
    toggledRentalItemAttachmentSessionStatus: false,
} as IRentalItemsAttachmentsSessionsState;

const rentalItemsAttachmentsSessionsSlice = createSlice({
    name: 'rentalItemsAttachmentsSessions',
    initialState,
    reducers: {
        clearRegRentalItemAttachmentSessionFieldErr: (state: IRentalItemsAttachmentsSessionsState, action: PayloadAction<string>) => {
            state.regRentalItemAttachmentSessionErrs[action.payload as keyof RegRentalItemAttachmentSessionErrs] = null;
        },
        clearRegRentalItemAttachmentSessionErrs: (state: IRentalItemsAttachmentsSessionsState) => {
            state.regRentalItemAttachmentSessionErrs = new RegRentalItemAttachmentSessionErrs();
        },
        tryResetRegRentalItemAttachmentSessionFormErr: (state: IRentalItemsAttachmentsSessionsState) => {
            let shouldReset = true;

            for (let key in state.regRentalItemAttachmentSessionErrs) {

                if (key === 'form') {
                    continue;
                }

                if (state.regRentalItemAttachmentSessionErrs[key as keyof RegRentalItemAttachmentSessionErrs]) {
                    shouldReset = false;
                    break;
                }
            }

            if (shouldReset) {
                state.regRentalItemAttachmentSessionErrs.form = null;
            }
        },
        resetRegisteredRentalItemAttachmentSession: (state: IRentalItemsAttachmentsSessionsState) => {
            state.registeredRentalItemAttachmentSession = false;
        },
        regRentalItemAttachmentSessionStart: (state: IRentalItemsAttachmentsSessionsState) => {
            state.regRentalItemAttachmentSessionErrs = new RegRentalItemAttachmentSessionErrs();
            state.registeringRentalItemAttachmentSession = true;
            state.registeredRentalItemAttachmentSession = false;
        },
        regRentalItemAttachmentSessionPass: (state: IRentalItemsAttachmentsSessionsState) => {
            state.registeringRentalItemAttachmentSession = false;
            state.registeredRentalItemAttachmentSession = true;
        },
        regRentalItemAttachmentSessionFail: (state: IRentalItemsAttachmentsSessionsState, action: PayloadAction<IFieldError[]>) => {
            state.registeringRentalItemAttachmentSession = false;

            const errs = action.payload;

            for (const err of errs) {
                for (const key in state.regRentalItemAttachmentSessionErrs) {
                    if (err.field === key) {
                        state.regRentalItemAttachmentSessionErrs[key as keyof RegRentalItemAttachmentSessionErrs] = err.msg;
                        break;
                    }
                }
            }


            if (!state.regRentalItemAttachmentSessionErrs.form) {
                state.regRentalItemAttachmentSessionErrs.form = 'Form incorrectly filled.';
            }
        },
        clearToggleRentalItemAttachmentSessionStatusErr: (state: IRentalItemsAttachmentsSessionsState) => {
            state.toggleRentalItemAttachmentSessionStatusErr = null;
        },
        resetToggledRentalItemAttachmentSessionStatus: (state: IRentalItemsAttachmentsSessionsState) => {
            state.toggledRentalItemAttachmentSessionStatus = false;
        },
        toggleRentalItemAttachmentSessionStatusStart: (state: IRentalItemsAttachmentsSessionsState) => {
            state.toggledRentalItemAttachmentSessionStatus = false;
            state.toggleRentalItemAttachmentSessionStatusErr = null;
            state.togglingRentalItemAttachmentSessionStatus = true;
        },
        toggleRentalItemAttachmentSessionStatusPass: (state: IRentalItemsAttachmentsSessionsState) => {
            state.toggledRentalItemAttachmentSessionStatus = true;
            state.togglingRentalItemAttachmentSessionStatus = false;
        },
        toggleRentalItemAttachmentSessionStatusFail: (state: IRentalItemsAttachmentsSessionsState, action: PayloadAction<string>) => {
            state.toggleRentalItemAttachmentSessionStatusErr = action.payload;
            state.togglingRentalItemAttachmentSessionStatus = false;
        },
    }
});

export const {
    clearRegRentalItemAttachmentSessionFieldErr,
    clearRegRentalItemAttachmentSessionErrs,
    tryResetRegRentalItemAttachmentSessionFormErr,
    resetRegisteredRentalItemAttachmentSession,
    regRentalItemAttachmentSessionStart,
    regRentalItemAttachmentSessionPass,
    regRentalItemAttachmentSessionFail,
    clearToggleRentalItemAttachmentSessionStatusErr,
    toggleRentalItemAttachmentSessionStatusStart,
    toggleRentalItemAttachmentSessionStatusPass,
    toggleRentalItemAttachmentSessionStatusFail,
    resetToggledRentalItemAttachmentSessionStatus,
} = rentalItemsAttachmentsSessionsSlice.actions;

interface ITryRegRentalItemAttachmentSessionArg {
    rentalItemAttachmentId: string;
    authToken: string;
}

export const tryRegRentalItemAttachmentSession = createAsyncThunk('rentalItemsAttachmentsSessions/tryRegRentalItemAttachmentSession', (arg: ITryRegRentalItemAttachmentSessionArg, { dispatch }) => {
    const endpoint = `${process.env.REACT_APP_SERVER_ENDPOINT}/api/rental-item-attachment-session`;

    const { authToken, rentalItemAttachmentId } = arg;

    dispatch(regRentalItemAttachmentSessionStart());

    axios.post(endpoint, { rentalItemAttachmentId }, {
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authToken}`
        }
    }).then(res => {
        socket.emit(APP_UPDATE_AVAILABLE, USER);

        dispatch(regRentalItemAttachmentSessionPass());
    }).catch(err => {

        const data = err?.response?.data;

        let fieldErrors: IFieldError[] = [];

        if (data?.errors) {
            fieldErrors = getFieldErrors(data.errors);
        } else if (data?.err) {
            fieldErrors = [{ field: 'form', msg: data.err }];
        } else {
            fieldErrors = [{ field: 'form', msg: 'Check your network connection.' }];
        }

        dispatch(regRentalItemAttachmentSessionFail(fieldErrors));
    })
});

interface ITryToggleRentalItemRentalItemAttachmentSessionStatusArg {
    authToken: string;
    rentalItemAttachmentSessionId: string;
    action: string;     // abort | complete | activate
}

export const tryToggleRentalRentalItemAttachmentSessionStatus = createAsyncThunk('rentalItems/tryToggleRentalItemAttachmentSessionStatus', (arg: ITryToggleRentalItemRentalItemAttachmentSessionStatusArg, { dispatch }) => {
    const { authToken, rentalItemAttachmentSessionId, action } = arg;

    const endpoint = `${process.env.REACT_APP_SERVER_ENDPOINT}/api/rental-items-attachments-sessions/${rentalItemAttachmentSessionId}/${action}`;

    dispatch(toggleRentalItemAttachmentSessionStatusStart())
    axios.post(endpoint, {}, {
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authToken}`
        }
    }).then(res => {
        dispatch(toggleRentalItemAttachmentSessionStatusPass());
    })
        .catch(err => {
            const data = err?.response?.data;
            let error = '';

            if (data?.errMsg) {
                error = data.errMsg;
            } else if (data?.errors) {
                error = getFieldErrors(data.errors)[0].msg;
            } else {
                error = 'Check your network connection.';
            }

            dispatch(toggleRentalItemAttachmentSessionStatusFail(error));
        });

})

export default rentalItemsAttachmentsSessionsSlice.reducer;